import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Container, Divider, Grid, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { activate2FA, register2FA, validatePassword } from '../../services/User';
import { getPublicIp } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

function TwoStepVerification(props) {

    const navigate = useNavigate();
    const {t} = useTranslation();

    /*start: 0 mensaje, 1 confirm pass, 2 create y generate QR */

    const [loadingStart, setLoadingStart] = useState(false);
    const [loadingPass, setLoadingPass] = useState(false);
    const [loadingActivate, setLoadingActivate] = useState(false);
    const [start, setStart] = useState(0);
    const [errValidPass, setErrValidPass] = useState(null);
    const [qr, setQr] = useState(null);
    const [id2fa, setId2fa] = useState(null);
    const [errActiveAuth, setErrActiveAuth] = useState(null);

    const handleStart2FA = () => {
        setLoadingStart(true);
        setStart(1);
        setLoadingStart(false);
    }

    //confirmar pass
    const confirmPassword = async (val, actions) => {
        setLoadingPass(true);
        //console.log("confirmar password", val, actions);

        const validation = await validatePassword(val.password);

        if (validation.error === false) {

            if (validation.ispass === true) {

                //create register 2FA
                const ip = await getPublicIp();
                const reg2fa = await register2FA(ip);

                if (reg2fa.error === false) {
                    //inicializar qr
                    setId2fa(reg2fa.id);
                    setQr(reg2fa.qr);
                    setStart(2);
                } else {
                    actions.resetForm();
                    setErrValidPass(t('supportError'));
                }

                setLoadingPass(false);
            } else {
                actions.resetForm();
                setErrValidPass(t('incorrectPassword'));
                setLoadingPass(false);
            }

        } else {
            setErrValidPass(t('incorrectPassword'));
            setLoadingPass(false);
        }
    };

    const initialValues = {
        password: ''
    };
    const validationSchema = Yup.object().shape({
        password: Yup.string()
            .required(t('passwordRequired'))
    });

    //activacion 2fa
    const enable2FA = async (val, actions) => {
        setLoadingActivate(true);

        const ip = await getPublicIp();
        //cambiar activate to 1 para habilitar auth2FA
        const status2fa = await activate2FA(id2fa, val.code, ip);

        if (status2fa.error === false) {
            if (status2fa.active === 1) {
                navigate('/clientarea/account/', { replace: true });
            } else {
                actions.resetForm();
                setErrActiveAuth(t('invalidCode'));
                setLoadingActivate(false);
            }
        } else {
            actions.resetForm();
            setErrActiveAuth(t('invalidCode'));
            setLoadingActivate(false);
        }

    }

    const initialActivate = {
        code: ''
    };
    const validationActivate = Yup.object().shape({
        code: Yup.string()
            .matches(/^[0-9]+$/, ()=>t('numbersOnly'))
            .required(()=>t('codeRequired'))
    });


    return (
        <>
            <Helmet>
                <title>{t('clientAreaTitle')}</title>
            </Helmet>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center'
                }}
            >

                {start === 0 &&

                    <Container maxWidth="md" variant='cont-nk'>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box mt={3} mb={3} >
                                    <Typography variant='h4'>{t('twoStepVerification')}</Typography>
                                    <Divider />
                                </Box>
                            </Grid>
                        </Grid>

                        <Grid container sx={{ border: "1px solid #dadce0", borderRadius: "5px", py: 3, px: 2, marginBottom: "3rem" }}>
                            <Grid item xs={12}>
                                <Typography >{t('twoStepVerificationDescription')}</Typography>

                                <Typography sx={{ fontWeight: 500, mt: 4, mb: 2 }}>Google Authenticator</Typography>
                                <Typography >
                                    <Trans
                                        i18nKey="installAuthenticatorApp"
                                        components={{
                                            1: <span style={{ fontWeight: "600" }} />
                                        }}
                                    />
                                </Typography>
                            </Grid>

                            <Grid container justifyContent="flex-end">
                                <LoadingButton loading={loadingStart} onClick={handleStart2FA} variant='contained' color='primary' sx={{ my: 2 }}>
                                    {t('start')}
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </Container>

                }

                {start === 1 &&

                    <Container maxWidth="sm" variant='cont-nk'>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box mt={3} mb={3} >
                                    <Typography variant='h4' sx={{ textAlign: "center" }}>{t('twoStepVerification')}</Typography>
                                    <Divider />
                                </Box>
                            </Grid>

                            <Grid item xs={12} >
                                <Typography sx={{ fontSize: "1.33rem" }}>{t('verifyIdentity')}</Typography>

                                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={confirmPassword}>
                                    {(props) => (

                                        <Form>
                                            <Grid container spacing={2} sx={{ pt: 4 }}>
                                                <Grid item xs={12}>

                                                    {errValidPass &&
                                                        <Box>
                                                            <Typography sx={{ color: 'red', paddingBottom: "1rem", fontSize: "1.2rem" }} >
                                                                {errValidPass}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    <Field as={TextField} variant="outlined" name='password' label={t('enterPassword')} type="password" fullWidth
                                                        error={props.errors.password && props.touched.password}
                                                        helperText={<ErrorMessage name='password' />} required />
                                                </Grid>

                                            </Grid>

                                            <Grid container justifyContent="flex-end" sx={{ pb: 4 }}>
                                                <LoadingButton loading={loadingPass} type='submit' variant='contained' color='primary' sx={{ my: 2 }}>
                                                    {t('next')}
                                                </LoadingButton>
                                            </Grid>

                                        </Form>

                                    )}
                                </Formik>

                            </Grid>

                        </Grid>
                    </Container>

                }

                {(start === 2 && qr !== null) &&
                    <Container maxWidth="sm" variant='cont-nk'>
                        <Grid container>
                            <Grid item xs={12}>
                                <Box mt={3} mb={3} >
                                    <Typography variant='h4' sx={{ textAlign: "center" }}>{t('twoStepVerification')}</Typography>
                                    <Divider />
                                </Box>
                            </Grid>

                            <Grid item xs={12} >
                                <Typography sx={{mt: 1}}><b>1.</b> {t('scanAuthenticatorQr')}</Typography>

                                <Grid align='center' sx={{mt: 2, mb: 3}}>
                                    <img src={qr} alt='code qr' align='center'></img>
                                </Grid>

                                <Typography><b>2.</b> {t('enterSixDigitVerificationCode')}</Typography>

                                <Formik initialValues={initialActivate} validationSchema={validationActivate} onSubmit={enable2FA}>
                                    {(props) => (

                                        <Form>
                                            <Grid container spacing={2} sx={{ pt: 4 }}>
                                                <Grid item xs={12}>
                                                    {errActiveAuth &&
                                                        <Box>
                                                            <Typography sx={{ color: 'red', paddingBottom: "1rem", fontSize: "1.2rem" }} >
                                                                {errActiveAuth}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    <Field as={TextField} variant="outlined" name='code' label={t('enterCode')} fullWidth
                                                        error={props.errors.code && props.touched.code}
                                                        helperText={<ErrorMessage name='code' />} required />
                                                </Grid>

                                            </Grid>

                                            <Grid container justifyContent="flex-end" sx={{ pb: 4 }}>
                                                <LoadingButton loading={loadingActivate} type='submit' variant='contained' color='primary' sx={{ my: 2 }}>
                                                    {t('confirm')}
                                                </LoadingButton>
                                            </Grid>

                                        </Form>

                                    )}
                                </Formik>
                            </Grid>


                        </Grid>
                    </Container>
                }


            </Box>
        </>
    );
}

export default TwoStepVerification;